import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  countriesByName, countryName, groupAsksItsOwn, HELD_STILL_KEPT, LETTER_FRAMES, letterFrame, lettersGo, parsePlace,
  placeKey, routesFor, TOWN_MAX,
  type CustomerAudience, type GroupView, type MarketView, type Place, type SetupResponse,
} from "@joogoos/contracts";
import { api } from "../../api.js";
import { Icon } from "../../ui/icons.js";
import { Card } from "./chrome.js";
import { AskForALink } from "./Letter.js";

/**
 * Where this business looks (Settings), and the form one group is written in (a group's Limits tab).
 *
 * The groups were cards here, on *Set up*, until 2026-11-04; they are screens of their own now (38, ADR 0068), and a
 * new one is made on *New group*. A place is a country or one town inside it (ADR 0043) — the plumber's four towns
 * and a shipper's three countries are the same shape, and neither is anybody's default.
 */

/**
 * Where this workspace looks: a country, or one town inside it
 * ([ADR 0043](../../../../../docs/adr/0043-reach-is-a-list-of-places-and-a-place-is-a-country-or-one-town-inside-it.md)).
 *
 * It asked for two letters typed into a box until 2026-09-21, which is a quiz nobody should have to pass to say
 * they sell to Germany — and `country()` takes any two letters, so a wrong guess was kept without a word. It is a
 * list of every country by name now, and the codes are what gets stored, as they always were.
 *
 * The same day it stopped being countries alone. A plumber does not sell to the United Kingdom; he drives to four
 * towns. So the country is chosen by name and a town may be typed beside it, and an empty town is the country
 * taken whole — which is what Overbill means and what every row written before that ADR already meant.
 *
 * *Who works which* — a member per country — was here too and went the same day: no screen read it and no endpoint
 * checked it, and one workspace writes every letter from one mailbox, so dividing its places decided nothing.
 */
export function Markets({ d, keep }: { d: SetupResponse; keep: (d: SetupResponse) => void }) {
  const [country, setCountry] = useState("");
  const [town, setTown] = useState("");
  const add = useMutation({ mutationFn: (p: Place) => api.addMarket(p.country, p.town), onSuccess: keep });
  const drop = useMutation({ mutationFn: (p: Place) => api.removeMarket(p.country, p.town), onSuccess: keep });
  // A country already taken whole is still offered, because a town inside it is a different place to look.
  const held = new Set(d.markets.map(placeKey));
  const offer = countriesByName();

  const said = (p: Place) => (p.town ? `${p.town}, ${countryName(p.country)}` : countryName(p.country));
  // The places whose law holds every letter, said once per country however many towns are in it (ADR 0065). They
  // stay places: what is found there can be kept for an introduction or a call, and the loop leaves them alone.
  const lawHolds = [...new Set(d.markets.map((m) => m.country))]
    .map((c) => ({ country: c, law: lettersGo(c) }))
    .flatMap((h) => (h.law.goes ? [] : [{ country: h.country, says: h.law.says }]));
  const choosing = country ? lettersGo(country) : null;
  const addNow = () => {
    const p = { country, town: town.trim() };
    if (country && !held.has(placeKey(p))) add.mutate(p);
    setCountry("");
    setTown("");
  };

  return (
    <Card title="Where you look" aside={<span className="text-xs text-muted">a country, or a town in one</span>}>
      <p className="text-xs text-muted leading-relaxed">
        A plumber looks in the towns he drives to; a business that sells at a distance looks in whole countries.
        Choose a country on its own to search all of it, or name a town to search just there.
      </p>
      <div className="flex flex-wrap gap-1.5 items-center">
        {d.markets.map((m) => (
          <span key={placeKey(m)} className="px-2.5 py-1 rounded-lg bg-page border border-line-2 text-xs font-medium flex items-center gap-1.5">
            {said(m)}
            {lettersGo(m.country).goes ? null : <span className="text-[10.5px] font-normal text-accent">· no letters</span>}
            {d.mayEdit ? (
              <button aria-label={`Remove ${said(m)}`} onClick={() => drop.mutate(m)} className="text-faint hover:text-bad"><Icon name="x" size={11} /></button>
            ) : null}
          </span>
        ))}
      </div>
      {lawHolds.length ? (
        <div className="flex flex-col gap-1" role="note">
          {lawHolds.map((h) => (
            <p key={h.country} className="text-xs text-muted leading-relaxed">
              <b className="text-ink">{countryName(h.country)}</b>: {h.says} {HELD_STILL_KEPT}
            </p>
          ))}
        </div>
      ) : null}
      {d.mayEdit ? (
        <div className="flex items-center gap-1.5">
          <select
            value={country} aria-label="Add a country" disabled={add.isPending}
            onChange={(e) => setCountry(e.target.value)}
            className="min-w-0 flex-1 px-2 py-1 rounded-lg border border-dashed border-line-2 bg-panel text-xs"
          >
            <option value="">Add a country…</option>
            {offer.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
          <input
            value={town} onChange={(e) => setTown(e.target.value.slice(0, TOWN_MAX))}
            aria-label="And a town, or leave it empty for the whole country" placeholder="a town, or all of it"
            className="min-w-0 flex-1 px-2 py-1 rounded-lg border border-dashed border-line-2 bg-panel text-xs"
          />
          <button onClick={addNow} disabled={!country || add.isPending} className="text-xs font-medium text-coral disabled:text-muted">Add</button>
        </div>
      ) : null}
      {/* Said before the press, so nobody adds a place expecting letters to go there (ADR 0065). */}
      {choosing && !choosing.goes ? (
        <p className="text-xs text-accent leading-relaxed" role="status">
          {choosing.says} You can still add it, to find companies for an introduction or a call.
        </p>
      ) : null}
      {d.suggestedMarkets.length ? (
        <p className="text-xs text-muted">
          Your business page suggests{" "}
          {d.suggestedMarkets.map((c) => (
            <button key={c} onClick={() => add.mutate({ country: c, town: "" })} disabled={!d.mayEdit} className="font-semibold text-coral disabled:text-muted mx-0.5">{countryName(c)}</button>
          ))}
          .
        </p>
      ) : null}
    </Card>
  );
}

/**
 * Adding or editing one group.
 *
 * It rendered under the whole grid until 2026-09-21, which with eight groups put it a screen and a half below the
 * *Add one* that opened it: the button looked broken because nothing a person could see had changed. It opens
 * where the press was now — at the top of the grid for a new one, in place of the card for an edit — and it says
 * why a save was refused, which it also used not to.
 */
export function GroupForm({ group, markets, business, answersSeen = true, letter = true, onSave, onCancel, busy, error }: {
  group: GroupView | null;
  /** Whether its own letter is asked for here. A group's Limits tab leaves it to the Letters tab (38). */
  letter?: boolean;
  /** Every place this workspace looks in, as the chips a group is narrowed to. */
  markets: MarketView[];
  /** The usual letter, in Settings, which an empty field here falls back to and so shows as its placeholder. */
  business?: { offer: string; ask: string; languages: string[] };
  /** Whether answers reach Joogoos, so the ask is advised to be a link when they do not (`AskForALink`). */
  answersSeen?: boolean;
  onSave: (g: unknown) => void;
  onCancel: () => void;
  busy: boolean;
  error?: unknown;
}) {
  const [name, setName] = useState(group?.name ?? "");
  const [looksFor, setLooksFor] = useState(group?.looksFor ?? "");
  const [why, setWhy] = useState(group?.why ?? "");
  const [audience, setAudience] = useState<CustomerAudience>(group?.audience ?? "business");
  const [route, setRoute] = useState(group?.route ?? "direct");
  // A group's places are `placeKey` strings. A new one starts in none: it used to start with every place the
  // workspace looks in ticked, and a UK group made that way was also looked for in France and the Gulf (2026-11-04).
  const [countries, setCountries] = useState<string[]>(group?.countries ?? []);
  const allowed = routesFor(audience);
  const chosenRoute = allowed.includes(route) ? route : allowed[0]!;
  // A direct group asks what Set up says; anything else asks the business holding the people we want, which is a
  // different sentence and the whole reason the group exists.
  const ownAsk = groupAsksItsOwn(chosenRoute);
  // The same two minimums the API holds. Said here as a disabled button rather than as a 400 nobody asked for.
  const ready = name.trim().length >= 2 && looksFor.trim().length >= 5 && countries.length > 0;

  return (
    <form
      className="sm:col-span-2 rounded-lg border border-coral/40 bg-page p-3 flex flex-col gap-2.5"
      onSubmit={(e) => { e.preventDefault(); onSave({ name, looksFor, why, audience, route: chosenRoute, countries }); }}
    >
      <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-coral">
        {group ? `Editing ${group.name}` : "A new group"}
      </span>
      <label className="flex flex-col gap-1">
        <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} className="px-2.5 py-1.5 rounded-lg border border-line-2 bg-panel text-[13px]" />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">Looks for</span>
        <textarea value={looksFor} onChange={(e) => setLooksFor(e.target.value)} rows={2} className="px-2.5 py-1.5 rounded-lg border border-line-2 bg-panel text-[13px]" />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">Why they fit</span>
        <input value={why} onChange={(e) => setWhy(e.target.value)} className="px-2.5 py-1.5 rounded-lg border border-line-2 bg-panel text-[13px]" />
      </label>
      <div className="flex flex-wrap gap-4">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">Who they are</span>
          <select value={audience} onChange={(e) => setAudience(e.target.value as CustomerAudience)} className="px-2.5 py-1.5 rounded-lg border border-line-2 bg-panel text-[13px]">
            <option value="business">a business</option>
            <option value="public">the public</option>
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">How we reach them</span>
          <select value={chosenRoute} onChange={(e) => setRoute(e.target.value as GroupView["route"])} className="px-2.5 py-1.5 rounded-lg border border-line-2 bg-panel text-[13px]">
            {allowed.map((r) => (
              <option key={r} value={r}>
                {r === "direct" ? "write to them" : r === "through_business" ? "through the businesses that hold them" : "a list you bring"}
              </option>
            ))}
          </select>
        </label>
      </div>
      {audience === "public" ? (
        <p className="text-xs text-muted leading-relaxed">
          Writing to a person who has not asked is unlawful in nearly every market, so this group is never written to
          directly. It reaches its people through the businesses that already have them, or through a list you bring.
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted mr-1">Where</span>
        {markets.map((m) => {
          const k = placeKey(m);
          return (
            <button
              key={k} type="button"
              onClick={() => setCountries((now) => now.includes(k) ? now.filter((c) => c !== k) : [...now, k])}
              className={`px-2 py-0.5 rounded-lg text-xs font-medium border ${countries.includes(k) ? "bg-ink text-white border-ink" : "bg-panel border-line-2 text-muted"}`}
            >
              {m.town || countryName(m.country)}
            </button>
          );
        })}
        {!markets.length ? <span className="text-xs text-muted">Say where you look, in Settings, first.</span> : null}
      </div>
      {error ? <p className="text-xs text-bad">{String(error)}</p> : null}
      <div className="flex items-center gap-2">
        <button type="submit" disabled={busy || !ready} className="px-3 py-1.5 rounded-lg bg-ink text-white text-xs font-semibold disabled:opacity-40">Save</button>
        <button type="button" onClick={onCancel} className="px-3 py-1.5 rounded-lg border border-line-2 text-xs font-medium">Cancel</button>
        {!ready ? <span className="text-xs text-muted">A name, a line saying what to look for, and at least one place.</span> : null}
      </div>
    </form>
  );
}
